import type { CustomFieldExportColumn } from "@/domain/customFieldExport";
import type { InterventionRecord } from "@/domain/interventions";
import type { Answers, QuestionnaireResponse } from "@/domain/questionnaire";
import type { InterventionStatus, SourceRow } from "@/domain/types";
import { splitWorkingComments, WORKING_COMMENT_COLUMN } from "@/domain/workingComments";
import { getSupabase, unwrap } from "./supabaseClient";

interface InterventionRow {
  id: string;
  item_number: string | number | null;
  status: InterventionStatus;
  submitted_by: string | null;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  questionnaire_id: string | null;
  questionnaire_version: number | null;
  row_data: SourceRow;
}

/**
 * Active interventions visible to the caller. Row Level Security decides
 * which rows come back (own location, own submissions, or everything).
 */
export async function fetchInterventions(): Promise<InterventionRecord[]> {
  const rows = unwrap(
    await getSupabase()
      .from("interventions")
      .select(
        "id, item_number, status, submitted_by, created_at, created_by, updated_at, updated_by, questionnaire_id, questionnaire_version, row_data",
      )
      .eq("is_active", true),
  ) as InterventionRow[];
  return rows.map((row) => ({
    row: row.row_data,
    meta: {
      dbId: row.id,
      status: row.status,
      submittedBy: row.submitted_by,
      createdAt: row.created_at,
      createdBy: row.created_by,
      updatedAt: row.updated_at,
      updatedBy: row.updated_by,
      questionnaire:
        row.questionnaire_id && row.questionnaire_version
          ? { id: row.questionnaire_id, version: row.questionnaire_version }
          : null,
    },
  }));
}

export async function fetchPrivateResponse(
  interventionId: string,
): Promise<QuestionnaireResponse | null> {
  return unwrap(
    await getSupabase()
      .from("intervention_responses")
      .select("questionnaire_id, questionnaire_version, answers")
      .eq("intervention_id", interventionId)
      .maybeSingle(),
  ) as QuestionnaireResponse | null;
}

/** Administrators' internal comment on an intervention; "" when there is none. */
export async function fetchWorkingComment(interventionId: string): Promise<string> {
  const row = unwrap(
    await getSupabase()
      .from("intervention_comments")
      .select("comment")
      .eq("intervention_id", interventionId)
      .maybeSingle(),
  ) as { comment: string } | null;
  return row?.comment ?? "";
}

/** Saves the comment; an empty comment removes it. Row Level Security limits this to administrators. */
export async function saveWorkingComment(interventionId: string, comment: string): Promise<void> {
  const table = getSupabase().from("intervention_comments");
  const text = comment.trim();
  unwrap(
    text
      ? await table.upsert(
          { intervention_id: interventionId, comment: text },
          { onConflict: "intervention_id" },
        )
      : await table.delete().eq("intervention_id", interventionId),
  );
}

export interface LocationQuestionnaire {
  name: string;
  questionnaire_id: string;
  questionnaire_version: number;
}

export async function fetchLocationQuestionnaires(): Promise<LocationQuestionnaire[]> {
  return unwrap(
    await getSupabase()
      .from("locations")
      .select("name, questionnaire_id, questionnaire_version")
      .order("name"),
  ) as LocationQuestionnaire[];
}

export interface SaveInterventionInput {
  interventionId: string | null;
  location: string;
  rowData: SourceRow;
  questionnaireId: string;
  questionnaireVersion: number;
  /** `null` keeps an admin-created intervention without a private response. */
  answers: Answers | null;
  /** Respondents choose draft/submitted; administrators pass `null`. */
  status: "draft" | "submitted" | null;
}

/**
 * Saves the public row and the private answers in one transaction via the
 * `save_intervention_questionnaire` RPC, which re-checks authorization.
 */
export async function saveIntervention(input: SaveInterventionInput): Promise<string> {
  const id = unwrap(
    await getSupabase().rpc("save_intervention_questionnaire", {
      p_intervention_id: input.interventionId,
      p_location: input.location,
      p_row_data: input.rowData,
      p_questionnaire_id: input.questionnaireId,
      p_questionnaire_version: input.questionnaireVersion,
      p_answers: input.answers,
      p_status: input.status,
    }),
  ) as string | null;
  if (!id) throw new Error("No intervention ID returned");
  return id;
}

/**
 * Saves only Skjemadata for administrators. The database reads and locks the
 * existing public row itself; the client never sends hidden/unfinished display
 * fields or overwrites another editor's public changes.
 */
/** Saves display information and explicitly records whether its title was edited. */
export async function saveAdminDisplayInformation(input: {
  interventionId: string | null;
  location: string;
  rowData: SourceRow;
  questionnaireId: string;
  questionnaireVersion: number;
  titleOverride: boolean | null;
}): Promise<string> {
  const id = unwrap(
    await getSupabase().rpc("save_admin_display_information", {
      p_intervention_id: input.interventionId,
      p_location: input.location,
      p_row_data: input.rowData,
      p_questionnaire_id: input.questionnaireId,
      p_questionnaire_version: input.questionnaireVersion,
      p_title_override: input.titleOverride,
    }),
  ) as string | null;
  if (!id) throw new Error("No intervention ID returned");
  return id;
}

export async function saveAdminQuestionnaireOnly(input: {
  interventionId: string | null;
  location: string;
  questionnaireId: string;
  questionnaireVersion: number;
  answers: Answers;
}): Promise<string> {
  const id = unwrap(
    await getSupabase().rpc("save_admin_questionnaire_only", {
      p_intervention_id: input.interventionId,
      p_location: input.location,
      p_questionnaire_id: input.questionnaireId,
      p_questionnaire_version: input.questionnaireVersion,
      p_answers: input.answers,
    }),
  ) as string | null;
  if (!id) throw new Error("No intervention ID returned");
  return id;
}

export async function setInterventionStatus(
  interventionId: string,
  status: "approved" | "draft",
): Promise<void> {
  unwrap(await getSupabase().from("interventions").update({ status }).eq("id", interventionId));
}

/**
 * Soft-deletes (is_active = false). Superadmins update directly; respondents
 * go through `delete_own_intervention`, which enforces ownership.
 */
export async function softDeleteIntervention(
  interventionId: string,
  asSuperadmin: boolean,
): Promise<void> {
  const supabase = getSupabase();
  if (asSuperadmin) {
    unwrap(
      await supabase.from("interventions").update({ is_active: false }).eq("id", interventionId),
    );
  } else {
    unwrap(await supabase.rpc("delete_own_intervention", { p_intervention_id: interventionId }));
  }
}

const EXPORT_PAGE_SIZE = 500;
/** IDs per `in (...)` filter, keeping request URLs short. */
const ID_CHUNK_SIZE = 200;

/**
 * Active intervention rows in import format, optionally for one location.
 * RLS limits local administrators to their own location. `ID` is the source
 * item number when there is one, so re-importing updates instead of duplicating.
 */
/** Working comments for the given interventions (only those RLS lets the caller read). */
async function fetchWorkingComments(interventionIds: string[]): Promise<Map<string, string>> {
  const comments = new Map<string, string>();
  for (let start = 0; start < interventionIds.length; start += ID_CHUNK_SIZE) {
    const page = unwrap(
      await getSupabase()
        .from("intervention_comments")
        .select("intervention_id, comment")
        .in("intervention_id", interventionIds.slice(start, start + ID_CHUNK_SIZE)),
    ) as { intervention_id: string; comment: string }[];
    for (const row of page) comments.set(row.intervention_id, row.comment);
  }
  return comments;
}

export async function fetchInterventionsForExport(
  location: string | null,
  customFields: CustomFieldExportColumn[] = [],
): Promise<SourceRow[]> {
  const records: (Pick<InterventionRow, "id" | "item_number" | "row_data"> & {
    source_item_number: string | null;
    location: string;
  })[] = [];
  for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
    let query = getSupabase()
      .from("interventions")
      .select("id, item_number, source_item_number, location, row_data")
      .eq("is_active", true);
    if (location !== null) query = query.eq("location", location);
    const page = unwrap(
      await query.order("item_number").range(offset, offset + EXPORT_PAGE_SIZE - 1),
    ) as typeof records;
    records.push(...page);
    if (page.length < EXPORT_PAGE_SIZE) break;
  }
  const interventionIds = records.map((record) => record.id);
  // Both tables are RLS-filtered. Only authorized administrators can export
  // admin-only values, and local administrators are scoped to their location.
  const [comments, customValues] = await Promise.all([
    fetchWorkingComments(interventionIds),
    customFields.length
      ? fetchCustomValuesForExport(interventionIds)
      : Promise.resolve(new Map<string, Map<string, string>>()),
  ]);
  return records.map((record) => {
    const row: SourceRow = {
      ...record.row_data,
      ID: record.source_item_number || record.item_number,
    };
    const comment = comments.get(record.id);
    if (comment) row[WORKING_COMMENT_COLUMN] = comment;
    for (const field of customFields) {
      row[field.header] =
        field.location === null || field.location === record.location
          ? (customValues.get(record.id)?.get(field.id) ?? "")
          : "";
    }
    return row;
  });
}

/** Paginate both IDs and rows to avoid PostgREST's default result limit. */
async function fetchCustomValuesForExport(
  interventionIds: string[],
): Promise<Map<string, Map<string, string>>> {
  const result = new Map<string, Map<string, string>>();
  for (let start = 0; start < interventionIds.length; start += ID_CHUNK_SIZE) {
    const chunk = interventionIds.slice(start, start + ID_CHUNK_SIZE);
    for (let offset = 0; ; offset += EXPORT_PAGE_SIZE) {
      const values = unwrap(
        await getSupabase()
          .from("intervention_custom_values")
          .select("intervention_id, field_id, value")
          .in("intervention_id", chunk)
          .order("intervention_id")
          .order("field_id")
          .range(offset, offset + EXPORT_PAGE_SIZE - 1),
      ) as { intervention_id: string; field_id: string; value: string }[];
      for (const entry of values) {
        const byField = result.get(entry.intervention_id) ?? new Map<string, string>();
        byField.set(entry.field_id, entry.value);
        result.set(entry.intervention_id, byField);
      }
      if (values.length < EXPORT_PAGE_SIZE) break;
    }
  }
  return result;
}

/**
 * Superadmin bulk import of a local workbook (upsert by source item number).
 * The working-comment column is stored in `intervention_comments`, not in the
 * public `row_data`; when the workbook has that column, an empty cell removes
 * the comment.
 */
export async function importInterventionRows(rows: SourceRow[]): Promise<number> {
  const { rows: publicRows, comments } = splitWorkingComments(rows);
  const records = publicRows
    .map((row) => ({
      source_item_number: String(row.ID ?? "").trim(),
      location: String(row.Region ?? "").trim() || null,
      source: "excel_import",
      is_active: true,
      row_data: row,
    }))
    .filter((record) => record.source_item_number);
  if (!records.length) return 0;
  const saved = unwrap(
    await getSupabase()
      .from("interventions")
      .upsert(records, { onConflict: "source_item_number" })
      .select("id, source_item_number"),
  ) as { id: string; source_item_number: string }[];
  if (comments) await saveImportedComments(saved, comments);
  return records.length;
}

async function saveImportedComments(
  saved: { id: string; source_item_number: string }[],
  comments: Map<string, string>,
): Promise<void> {
  const upserts: { intervention_id: string; comment: string }[] = [];
  const removals: string[] = [];
  for (const record of saved) {
    const comment = comments.get(record.source_item_number);
    if (comment) upserts.push({ intervention_id: record.id, comment: comment.slice(0, 10000) });
    else if (comment === "") removals.push(record.id);
  }
  const table = () => getSupabase().from("intervention_comments");
  if (upserts.length) unwrap(await table().upsert(upserts, { onConflict: "intervention_id" }));
  for (let start = 0; start < removals.length; start += ID_CHUNK_SIZE)
    unwrap(
      await table()
        .delete()
        .in("intervention_id", removals.slice(start, start + ID_CHUNK_SIZE)),
    );
}
